import type { NASecurityObject, NAMTObject } from '@/api/models';
import { NAMTMessageType } from '@/types/enums';
import { useFieldPending } from '@/hooks';
import { SectionCard, ToggleSwitch, SelectDropdown, PendingBadge } from '@/components/common/ConfigControls';
import {
  findPendingMT,
  deviceZoneTypeOptions,
  deviceZoneCategoryOptions,
  ZONE_TYPE_CATEGORY_OPTIONS,
  HIDE_INPUT_TYPE_FOR_ZONE_TYPES,
  toOrdinal,
  LEGACY_ALARM_TYPE_STRING_MAP,
  LEGACY_DEVICE_CATEGORY_STRING_MAP,
} from '@/lib/sensor-commands';

const ZONE_NUMS = [1, 2, 3, 4];

// Mirrors DeviceConfigPageViewModel.cs's refreshDeviceZones() — the device's own
// 4 built-in zone slots (sourceAddressLSB === 1), distinct from any wireless
// sensor's zones. Each zoneNum has a fixed, different set of allowed types (see
// DEVICE_ZONE_TYPE_MAP), and the zone-type-change command sequence here is NOT
// the same as xPulse's: this page pre-sends Normally-Open when switching to
// Water/Geo-Arm-Disarm (to avoid an invalid device state) and does NOT auto-fire
// a GET_SECURITY_DATA refresh afterward — both differ from XPulseSensorConfig.
export function DeviceZoneSettings({
  securityZones,
  mtObjList,
  sendCommand,
  onSetZoneCategory,
}: {
  securityZones: NASecurityObject[];
  mtObjList: NAMTObject[];
  sendCommand: (cmd: NAMTMessageType, dataStrList?: string[]) => Promise<boolean>;
  onSetZoneCategory: (zoneNum: number, deviceCategory: number) => Promise<void>;
}) {
  const fieldPending = useFieldPending();

  return (
    <SectionCard title="Zone Settings">
      <div className="space-y-4">
        {ZONE_NUMS.map((zoneNum) => {
          const zone = securityZones.find((z) => z.zoneNum === zoneNum && z.sourceAddressLSB === 1);
          const typeOptions = deviceZoneTypeOptions(zoneNum);
          const showZoneType = typeOptions.length > 1;
          const zoneKey = String(zoneNum);
          const zoneTypeFieldKey = `zoneType-${zoneNum}`;
          const zoneCategoryFieldKey = `zoneCategory-${zoneNum}`;
          const inputTypeFieldKey = `inputType-${zoneNum}`;

          const pendingZoneType = findPendingMT(mtObjList, NAMTMessageType.SET_SECURITY_ZONE_TYPE, (d) => d[0] === zoneKey);

          if (!zone) {
            return (
              <div key={zoneNum} className="rounded-lg border border-slate-200 bg-white p-3 dark:border-navy-600 dark:bg-navy-900">
                <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Zone {zoneNum}</p>
                <p className="mt-1 text-xs text-slate-400">No data reported for this zone yet.</p>
              </div>
            );
          }

          const zoneTypeOrdinal =
            fieldPending.getOptimisticValue<number>(zoneTypeFieldKey) ??
            (pendingZoneType ? Number(pendingZoneType.dataStrList?.[1]) : toOrdinal(zone.alarmType, LEGACY_ALARM_TYPE_STRING_MAP));

          const categoryOptions = deviceZoneCategoryOptions(zoneNum, zoneTypeOrdinal ?? -1);
          // Zones 3/4 offer Security with the full category list; zones 1/2 offer
          // Security too but with an intentionally empty list (no category picker).
          const showCategory = categoryOptions.length > 0;
          const categoryOrdinal =
            fieldPending.getOptimisticValue<number>(zoneCategoryFieldKey) ??
            toOrdinal(zone.deviceCategory, LEGACY_DEVICE_CATEGORY_STRING_MAP);

          const pendingInputType = findPendingMT(mtObjList, NAMTMessageType.SET_SECURITY_ZONE_INPUT_TYPE, (d) => d[0] === zoneKey);
          const isNC =
            fieldPending.getOptimisticValue<boolean>(inputTypeFieldKey) ??
            (pendingInputType ? pendingInputType.dataStrList?.[1] === '1' : zone.isNC);
          const showInputType = !HIDE_INPUT_TYPE_FOR_ZONE_TYPES.includes(zoneTypeOrdinal ?? -1);

          return (
            <div key={zoneNum} className="rounded-lg border border-slate-200 bg-white p-3 dark:border-navy-600 dark:bg-navy-900">
              <p className="mb-2 text-sm font-bold text-slate-700 dark:text-slate-200">Zone {zoneNum}</p>

              <div className={`grid gap-3 text-xs ${showCategory || showInputType || showZoneType ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {showZoneType && (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Zone Type</span>
                    <div className="flex items-center">
                      <SelectDropdown
                        value={String(zoneTypeOrdinal ?? '')}
                        options={typeOptions.map((opt) => ({ value: String(opt.value), label: opt.label }))}
                        onChange={(val) =>
                          fieldPending.run(zoneTypeFieldKey, Number(val), async () => {
                            const newOrdinal = Number(val);
                            if (HIDE_INPUT_TYPE_FOR_ZONE_TYPES.includes(newOrdinal)) {
                              // Avoid an invalid device state before switching to
                              // Water/Geo-Arm-Disarm — mirrors zoneTypeChangedCallback exactly.
                              await sendCommand(NAMTMessageType.SET_SECURITY_ZONE_INPUT_TYPE, [zoneKey, '0']);
                            }
                            await sendCommand(NAMTMessageType.SET_SECURITY_ZONE_TYPE, [zoneKey, val]);
                          })
                        }
                      />
                      {(fieldPending.isPending(zoneTypeFieldKey) || pendingZoneType) && <PendingBadge />}
                    </div>
                  </div>
                )}

                {showCategory && (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Zone Type Category</span>
                    <div className="flex items-center">
                      <SelectDropdown
                        value={String(categoryOrdinal ?? '')}
                        options={ZONE_TYPE_CATEGORY_OPTIONS.map((opt) => ({ value: String(opt.value), label: opt.label }))}
                        disabled={fieldPending.isPending(zoneCategoryFieldKey)}
                        onChange={(val) =>
                          fieldPending.run(zoneCategoryFieldKey, Number(val), () => onSetZoneCategory(zoneNum, Number(val)))
                        }
                      />
                      {fieldPending.isPending(zoneCategoryFieldKey) && <PendingBadge />}
                    </div>
                  </div>
                )}

                {showInputType && (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Zone Configuration</span>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-slate-700 dark:text-slate-300">{isNC ? 'Normally Closed' : 'Normally Open'}</span>
                      <ToggleSwitch
                        enabled={isNC}
                        loading={false}
                        onToggle={() =>
                          fieldPending.run(inputTypeFieldKey, !isNC, async () => {
                            await sendCommand(NAMTMessageType.SET_SECURITY_ZONE_INPUT_TYPE, [zoneKey, isNC ? '0' : '1']);
                            await sendCommand(NAMTMessageType.GET_SECURITY_DATA);
                          })
                        }
                      />
                      {(fieldPending.isPending(inputTypeFieldKey) || pendingInputType) && <PendingBadge />}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}
